import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { locationsGetAllMine, menusGetById, menusUpdate } from "~/api-refs";
import { ModalLoadingDialog } from "~/components/shared/modal-loading-dialog";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { MultiCombobox } from "~/components/ui/multi-combobox";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute("/_authenticated/menus/$menuId/edit")({
  component: EditMenuModal,
});

interface EditMenuFormData {
  name: string;
  locations: Id<"locations">[];
}

function EditMenuModal() {
  const { menuId } = Route.useParams();
  const navigate = useNavigate();

  // Fetch menu data
  const { data: menu, isPending: menuLoading } = useQuery(
    convexQuery(menusGetById, {
      id: toConvexTableId("menus", menuId),
      includeLocations: true,
    })
  );

  // Fetch locations
  const { data: locations } = useQuery(convexQuery(locationsGetAllMine, {}));

  // Update mutation
  const updateMenuMutation = useMutation({
    mutationFn: (data: {
      menuId: Id<"menus">;
      name: string;
      locations: Id<"locations">[];
      addToFutureLocations: boolean;
    }) => {
      return convexClient.mutation(menusUpdate, data);
    },
    onSuccess: () => {
      ProductEvents.menuUpdated(menuId);
    },
  });

  const form = useForm<EditMenuFormData>({
    defaultValues: {
      name: "",
      locations: [],
    },
  });

  // Populate form when data loads
  useEffect(() => {
    if (menu?.locations) {
      form.reset({
        name: menu.name,
        locations: menu.locations,
      });
    }
  }, [menu, form]);

  const handleClose = () => {
    void navigate({ to: `/menus/${menuId}/categories` });
  };

  const handleSave = async (values: EditMenuFormData) => {
    if (updateMenuMutation.isPending) {
      return;
    }
    try {
      await updateMenuMutation.mutateAsync({
        menuId: toConvexTableId("menus", menuId),
        name: values.name,
        locations: values.locations,
        addToFutureLocations: values.locations.length === locations?.length,
      });
      handleClose();
    } catch (error) {
      logger.exception("menu_update_failed", error);
    }
  };

  if (menuLoading) {
    return (
      <ModalLoadingDialog
        message="Loading menu details..."
        onOpenChange={handleClose}
      />
    );
  }

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent noScrollPrevention>
        <DialogHeader className="items-center space-y-0">
          <DialogTitle>Edit Menu</DialogTitle>
          <DialogDescription className="text-base">
            Menus can be shared across multiple locations
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-5" onSubmit={form.handleSubmit(handleSave)}>
            <FormField
              control={form.control}
              disabled={!menu}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Menu name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              disabled={!locations}
              name="locations"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Locations</FormLabel>
                  <FormControl>
                    <MultiCombobox
                      groups={["default"]}
                      allOption={{
                        dropdownLabel: "All and future locations",
                        selectedLabel: "All and future locations",
                      }}
                      groupLabels={{
                        default: "Your locations",
                      }}
                      items={{
                        default: locations
                          ? locations.map((r) => ({
                              value: r.id,
                              label: r.name,
                              imageUrl:
                                r.logoUrl ?? "/images/avatar-placeholder.svg",
                            }))
                          : [],
                      }}
                      onValueChange={(val) => field.onChange(val.default)}
                      values={{
                        default: field.value ?? [],
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              className="w-full"
              disabled={updateMenuMutation.isPending}
              size="lg"
              type="submit"
            >
              Save
            </Button>
          </form>
        </Form>

        {/* V4.1: Route-based delete for URL context tracking */}
        <div>
          <p className="label mt-1">Delete Menu</p>
          <p className="body-sm text-foreground mt-2">
            This will permanently delete this menu. Shared categories and items
            in other menus will not be affected.
          </p>
          <Button
            className="mt-4 gap-1.5"
            onClick={() => navigate({ to: `/menus/${menuId}/delete` })}
            type="button"
            variant="outline"
          >
            <Trash2 className="size-4" />
            Delete Menu
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
